import React from 'react';
import * as ProgressBarUI from './PlanProgressBarUI';
import { globalFormat } from '../../../../utils/formatting';
import { formatProgressBarValue } from '../PlanUtils';

interface IProps {
  /** Title of the progress bar (e.g., "Storage Usage") */
  title: string;
  /** Start date in ISO format (YYYY-MM-DD) */
  fromDate?: string;
  /** End date in ISO format (YYYY-MM-DD) */
  toDate?: string;
  /** Minimum value on the scale */
  minValue?: number;
  /** Maximum value on the scale */
  maxValue: number;
  /** Current value to display */
  currentValue: number;
  /** Whether to display as unlimited */
  isUnlimited?: boolean;
}

const PlanProgressBar = ({
  title,
  fromDate,
  toDate,
  minValue = 0,
  maxValue,
  currentValue,
  isUnlimited = false,
}: IProps) => {
  const { locale, dateFormat } = globalFormat.getGlobalFormat();
  // Calculate percentage (only if not unlimited)
  const percentage = isUnlimited
    ? 100
    : Math.min(
        100,
        Math.max(0, ((currentValue - minValue) / (maxValue - minValue)) * 100),
      );

  const formattedFromDate = fromDate
    ? globalFormat.formatDate(fromDate, { dateFormat })
    : '';
  const formattedToDate = toDate
    ? globalFormat.formatDate(toDate, { dateFormat })
    : '';
  const periodText = `${formattedFromDate} - ${formattedToDate}`;

  const value = formatProgressBarValue({
    value: currentValue,
    max: maxValue,
    isUnlimited,
    locale,
  });

  const isExceeding = currentValue > maxValue && !isUnlimited;
  return (
    <ProgressBarUI.Container>
      {/* Title and Period */}
      <ProgressBarUI.HeaderRow>
        <ProgressBarUI.TitleContainer>
          <ProgressBarUI.Title>{title}</ProgressBarUI.Title>
          {fromDate && toDate && (
            <ProgressBarUI.Period>{periodText}</ProgressBarUI.Period>
          )}
        </ProgressBarUI.TitleContainer>
      </ProgressBarUI.HeaderRow>

      {/* Progress Bar */}
      <ProgressBarUI.ProgressContainer>
        <ProgressBarUI.ProgressFill
          $percentage={percentage}
          $isUnlimited={isUnlimited}
          $isExceeding={isExceeding}
        />
      </ProgressBarUI.ProgressContainer>

      {/* Values */}
      <ProgressBarUI.ValuesRow>
        <ProgressBarUI.Value>{value}</ProgressBarUI.Value>
        <ProgressBarUI.Percentage
          $isUnlimited={isUnlimited}
          $isExceeding={isExceeding}
        >
          {isUnlimited
            ? '∞'
            : `${globalFormat.formatNumber(percentage / 100, {
                locale,
                decimalPlace: 2,
                maximumFractionDigits: 2,
                style: 'percent',
              })}`}
        </ProgressBarUI.Percentage>
      </ProgressBarUI.ValuesRow>
    </ProgressBarUI.Container>
  );
};

export default PlanProgressBar;
